// Shared logic for the Dynamic Form Builder: field catalog, condition / validation / role evaluation.

export const FIELD_TYPES = [
  { type: "text", label: "Text", group: "Basic" },
  { type: "textarea", label: "Text Area", group: "Basic" },
  { type: "number", label: "Number", group: "Basic" },
  { type: "email", label: "Email", group: "Basic" },
  { type: "phone", label: "Phone Number", group: "Basic" },
  { type: "address", label: "Address", group: "Basic" },
  { type: "date", label: "Date", group: "Date & Time" },
  { type: "time", label: "Time", group: "Date & Time" },
  { type: "datetime", label: "Date & Time", group: "Date & Time" },
  { type: "dropdown", label: "Dropdown", group: "Choice" },
  { type: "multiselect", label: "Multi Select", group: "Choice" },
  { type: "radio", label: "Radio Button", group: "Choice" },
  { type: "checkbox", label: "Checkbox", group: "Choice" },
  { type: "file", label: "File Upload", group: "Advanced" },
  { type: "signature", label: "Signature", group: "Advanced" },
  { type: "readonly", label: "Read Only Field", group: "Advanced" },
  { type: "autofill", label: "Auto-Filled Field", group: "Advanced" },
  { type: "calculated", label: "Calculated Field", group: "Advanced" },
  { type: "section_header", label: "Section Header", group: "Advanced" },
];

export const CHOICE_TYPES = ["dropdown", "multiselect", "radio", "checkbox"];

export const LAYOUTS = [
  { value: "single", label: "Single Column" },
  { value: "two", label: "Two Column" },
  { value: "three", label: "Three Column" },
  { value: "accordion", label: "Accordion Sections" },
  { value: "wizard", label: "Wizard Steps" },
];

export const CONDITION_OPERATORS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "in", label: "is one of" },
  { value: "not_empty", label: "is filled" },
  { value: "empty", label: "is empty" },
  { value: "gt", label: "is greater than" },
  { value: "lt", label: "is less than" },
];

export const VALIDATION_RULES = [
  { value: "email", label: "Email format" },
  { value: "phone", label: "Phone format" },
  { value: "numeric", label: "Numeric only" },
  { value: "no_future", label: "Date cannot be future" },
  { value: "no_past", label: "Date cannot be past" },
  { value: "unique", label: "Unique value" },
];

export const ROLE_CHOICES = [
  { code: "admin", label: "Admin" },
  { code: "clinical_director", label: "Clinical Director" },
  { code: "hah_physician", label: "H@H Physician" },
  { code: "physician", label: "Physician" },
  { code: "hah_nurse", label: "H@H Nurse" },
  { code: "nurse", label: "Nurse" },
  { code: "hah_coordinator", label: "H@H Coordinator" },
  { code: "care_coordinator", label: "Care Coordinator" },
  { code: "hospital_coordinator", label: "Hospital Coordinator" },
  { code: "home_care_team", label: "Home Care Team" },
  { code: "facility_manager", label: "Facility Manager" },
  { code: "compliance", label: "Compliance" },
];

const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

export function evalCondition(cond, values) {
  if (!cond || !cond.field) return true;
  const v = values[cond.field];
  switch (cond.operator) {
    case "equals":
      if (Array.isArray(v)) return v.includes(cond.value);
      return String(v ?? "") === String(cond.value ?? "");
    case "not_equals":
      return String(v ?? "") !== String(cond.value ?? "");
    case "in": {
      const arr = Array.isArray(cond.value) ? cond.value : String(cond.value ?? "").split(",");
      if (Array.isArray(v)) return v.some((x) => arr.includes(x));
      return arr.includes(v);
    }
    case "not_empty":
      return !isEmpty(v);
    case "empty":
      return isEmpty(v);
    case "gt":
      return Number(v) > Number(cond.value);
    case "lt":
      return Number(v) < Number(cond.value);
    default:
      return true;
  }
}

export function isFieldVisible(field, values, role) {
  if (role && (field.hidden_roles || []).includes(role)) return false;
  if (role && (field.visible_roles || []).length && !(field.visible_roles || []).includes(role)) return false;
  const showConds = (field.conditions || []).filter((c) => c.action === "show");
  return showConds.every((c) => evalCondition(c, values));
}

export function isFieldRequired(field, values) {
  if (field.required) return true;
  return (field.conditions || []).filter((c) => c.action === "require").some((c) => evalCondition(c, values));
}

export function isFieldReadonly(field, role) {
  if (!role) return false;
  if ((field.readonly_roles || []).includes(role)) return true;
  if ((field.editable_roles || []).length && !(field.editable_roles || []).includes(role)) return true;
  return false;
}

export function isSectionVisible(section, values, role) {
  if (!section.active) return false;
  if (role && (section.visible_roles || []).length && !(section.visible_roles || []).includes(role)) return false;
  if (section.condition) return evalCondition(section.condition, values);
  return true;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s()-]{6,}$/;

export function validateForm(definition, values, role) {
  const errors = {};
  (definition.sections || []).forEach((sec) => {
    if (!isSectionVisible(sec, values, role)) return;
    (sec.fields || []).forEach((f) => {
      if (!f.active || f.type === "section_header") return;
      if (!isFieldVisible(f, values, role)) return;
      const v = values[f.name];
      const empty = isEmpty(v);
      if (isFieldRequired(f, values) && empty) {
        errors[f.name] = f.validation_message || `${f.label} is required.`;
        return;
      }
      if (empty) return;
      const vals = f.validations || [];
      if ((f.type === "email" || vals.includes("email")) && typeof v === "string" && !EMAIL_RE.test(v))
        errors[f.name] = f.validation_message || "Enter a valid email address.";
      if ((f.type === "phone" || vals.includes("phone")) && typeof v === "string" && !PHONE_RE.test(v))
        errors[f.name] = f.validation_message || "Enter a valid phone number.";
      if ((f.type === "number" || vals.includes("numeric")) && isNaN(Number(v)))
        errors[f.name] = f.validation_message || "Must be a number.";
      if (f.min_length && typeof v === "string" && v.length < f.min_length)
        errors[f.name] = `Must be at least ${f.min_length} characters.`;
      if (f.max_length && typeof v === "string" && v.length > f.max_length)
        errors[f.name] = `Must be at most ${f.max_length} characters.`;
      if (f.type === "number") {
        if (f.min_value != null && Number(v) < f.min_value) errors[f.name] = `Must be ≥ ${f.min_value}.`;
        if (f.max_value != null && Number(v) > f.max_value) errors[f.name] = `Must be ≤ ${f.max_value}.`;
      }
      if (vals.includes("no_future") && v && new Date(v) > new Date())
        errors[f.name] = "Date cannot be in the future.";
      if (vals.includes("no_past") && v && new Date(v) < new Date(new Date().toDateString()))
        errors[f.name] = "Date cannot be in the past.";
    });
  });
  return errors;
}
